<script>
	import { onMount } from "svelte";

	import { nanoid } from "nanoid";

	import TextInput from "./TextInput.svelte";
	import FormRow from "./FormRow.svelte";
	import FormColumn from "./FormColumn.svelte";
	import TextButton from "./buttons/TextButton.svelte";
	import Country from "./Country.svelte";

	export let idPrefix = "a" + nanoid(4);
	export let addressValid = null;
	export let manualEntry = null;
	export let googlePlaceJson = null;
	export let formattedAddress = null;
	export let subpremise = null;
	export let premise = null;
	export let street = null;
	export let suburb = null;
	export let state = null;
	export let postcode = null;
	export let country = null;
	export let lat = null;
	export let lng = null;
	export let placeId = null;
	export let url = null;
	export let disabled;
	export let required;

	let addressInput;
	let autocomplete;
	let uniqueId = "i" + nanoid(4);

	const australianStates = {
		"Australian Capital Territory": "ACT",
		"New South Wales": "NSW",
		"Northern Territory": "NT",
		Queensland: "QLD",
		"South Australia": "SA",
		Tasmania: "TAS",
		Victoria: "VIC",
		"Western Australia": "WA"
	};

	onMount(() => {
		if (!disabled && !manualEntry) {
			try {
				const defaultBounds = new google.maps.LatLngBounds(
					new google.maps.LatLng(-37.812474, 144.956944),
					new google.maps.LatLng(-37.812474, 144.956944)
				);

				const options = {
					bounds: defaultBounds,
					types: ["geocode"]
				};

				//  In case there is no selection made
				addressInput.addEventListener("change", (e) => {
					//addressInput.setAttribute("data-google-select", "false");
					addressValid = false;
				});

				autocomplete = new google.maps.places.Autocomplete(addressInput, options);

				google.maps.event.addListener(autocomplete, "place_changed", function () {
					//addressInput.setAttribute("data-google-select", "true");
					addressValid = true;

					var result = autocomplete.getPlace();

					if (result) {
						var components = {};
						result.address_components.forEach((item, i) => {
							components[item.types[0]] = item.long_name;
						});
						googlePlaceJson = JSON.stringify(components);

						subpremise = components["subpremise"] || null;
						premise = components["premise"] || null;
						street =
							(components["street_number"] ? components["street_number"] + " " : "") +
							(components["route"] ? components["route"] : "");
						suburb = components["locality"] || "";
						state = components["administrative_area_level_1"] || "";
						postcode = components["postal_code"] || "";
						country = components["country"];

						if (australianStates[state]) {
							state = australianStates[state];
						}

						formattedAddress = result.formatted_address;

						if (result.geometry) {
							lat = result.geometry.location.lat();
							lng = result.geometry.location.lng();
						}
						placeId = result.place_id;
						url = result.url;

						result = {};
					}
				});
			} catch (error) {
				console.error("Mapping Error:", error);
				googleError = true;
			}
		}
	});

	const initiaise = () => {
		if (manualEntry) return;

		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(function (position) {
				var geolocation = {
					lat: position.coords.latitude,
					lng: position.coords.longitude
				};
				var circle = new google.maps.Circle({ center: geolocation, radius: position.coords.accuracy });
				autocomplete.setBounds(circle.getBounds());
			});
		}
	};

	const toggleManualAddress = () => {
		manualEntry = !manualEntry;

		googlePlaceJson = null;
		formattedAddress = null;
		subpremise = null;
		premise = null;
		street = null;
		suburb = null;
		state = null;
		postcode = null;
		country = null;
		lat = null;
		lng = null;
		placeId = null;
		url = null;
	};

	$: {
		if (manualEntry) {
			formattedAddress = "";
			if (subpremise) {
				formattedAddress = subpremise;
			}
			if (premise) {
				formattedAddress = formattedAddress ? `${formattedAddress}, ${premise}` : premise;
			}
			if (street) {
				formattedAddress = formattedAddress ? `${formattedAddress}, ${street}` : street;
			}
			if (suburb) {
				formattedAddress = formattedAddress ? `${formattedAddress}, ${suburb}` : suburb;
			}
			if (state) {
				formattedAddress = formattedAddress ? `${formattedAddress} ${state}` : state;
			}
			if (postcode) {
				formattedAddress = formattedAddress ? `${formattedAddress} ${postcode}` : postcode;
			}
			if (country) {
				formattedAddress = formattedAddress ? `${formattedAddress}, ${country}` : country;
			}
		}
	}
</script>

<section id={uniqueId}>
	{#if !manualEntry}
		<FormRow class="form-group form-row">
			<FormColumn cols="12">
				<label for="{idPrefix}_address">
					Address

					{#if required}
						<span class="required">*</span>
					{/if}
				</label>
				<input
					{disabled}
					{required}
					type="text"
					bind:this={addressInput}
					bind:value={formattedAddress}
					class="form-control address"
					on:focus={initiaise}
					id="{idPrefix}_address"
					autocompleteaddress
					data-google-select={addressValid ? "true" : "false"}
				/>
			</FormColumn>
			<FormColumn cols="12" class="confirmation-hide">
				<TextButton on:click={() => toggleManualAddress()}>Enter address manually</TextButton>
			</FormColumn>
		</FormRow>
	{:else}
		<FormRow class="form-group form-row">
			<FormColumn cols="6">
				<TextInput {disabled} {required} label="Address" bind:value={street} id="{idPrefix}_street"></TextInput>
			</FormColumn>
			<FormColumn cols="6">
				<TextInput {disabled} {required} label="Suburb" bind:value={suburb} id="{idPrefix}_suburb"></TextInput>
			</FormColumn>
		</FormRow>

		<FormRow class="form-group form-row">
			<FormColumn cols="4">
				<TextInput {disabled} {required} label="State" bind:value={state} id="{idPrefix}_state"></TextInput>
			</FormColumn>
			<FormColumn cols="4">
				<TextInput {disabled} {required} label="Postcode" bind:value={postcode} id="{idPrefix}_postcode"
				></TextInput>
			</FormColumn>
			<FormColumn cols="4">
				<Country {disabled} label="Country" bind:value={country} {required} id="{idPrefix}_country"></Country>
			</FormColumn>
		</FormRow>
	{/if}
</section>

<style>
	input.address {
		appearance: none;
		border: solid 1px var(--color-border);
		border-radius: 1px;
		padding: var(--spacing-sm);
		transition: var(--transition-fast);
		height: 50px;
		font-size: 18px;
		line-height: 26px;
		color: #333;
	}

	input.address:focus {
		border-color: var(--color-primary);
	}

	input.address:focus-visible {
		outline-offset: 0px;
		outline: -webkit-focus-ring-color auto 1px;
	}
</style>
